import React, { lazy } from "react";
import { Route } from "react-router-dom";
import permissions from "./permissions";
import AuthGuard from "gaurds/AuthGuard";
import AdminLayout from "layouts/AdminLayout";
import PermissionGuard from "../gaurds/PermissionGuard";
import Permissions from "./permissions";

// --- Lazy imports below ---
const Home = lazy(() => import("../pages/home/Home"));

// Inbound Orders
const InboundOrders = lazy(() => import("../pages/orders/pages/InboundOrder/pages"));
const InboundOrdersReceiveByOrder = lazy(() => import("../pages/orders/pages/InboundOrder/pages/receiveByOrder"));
const InboundOrdersAdhoc = lazy(() => import("../pages/orders/pages/InboundOrder/pages/adhoc"));
const InboundOrdersAddToStock = lazy(() => import("../pages/orders/pages/InboundOrder/pages/addToStock"));
const InboundOrdersComplyReceive = lazy(() => import("../pages/orders/pages/InboundOrder/pages/complyReceive"));
const InboundOrdersBlindReceive = lazy(() => import("../pages/orders/pages/InboundOrder/pages/blindReceive"));

const AddInboundOrder = lazy(() => import("../pages/orders/pages/InboundOrder/pages/receiveByOrder/add"));
const EditInboundOrder = lazy(() => import("../pages/orders/pages/InboundOrder/pages/receiveByOrder/edit"));
const SplitInboundOrder = lazy(() => import("../pages/orders/pages/InboundOrder/pages/receiveByOrder/split"));
const ScanReceiveByOrderInboundOrder = lazy(() => import("../pages/orders/pages/InboundOrder/pages/receiveByOrder/scan"));

const ScanInboundAdhoc = lazy(() => import("../pages/orders/pages/InboundOrder/pages/adhoc/scan"));
const AdhocWithoutSupplier = lazy(() => import("../pages/orders/pages/InboundOrder/pages/adhoc/withoutSupplier"));
const AddComplyReceive = lazy(() => import("../pages/orders/pages/InboundOrder/pages/complyReceive/addComplyReceive"));
const ScanBlindReceive = lazy(() => import("../pages/orders/pages/InboundOrder/pages/blindReceive/scan"));
const AggregationBlindReceive = lazy(() => import("../pages/orders/pages/InboundOrder/pages/blindReceive/aggregation"));

// Outbound Orders
const OutboundOrders = lazy(() => import("../pages/orders/pages/OutboundOrder/pages"));
const OutboundOrdersShipOutByOrder = lazy(() => import("../pages/orders/pages/OutboundOrder/pages/shipOutOrders"));
const OutboundOrdersAdhoc = lazy(() => import("../pages/orders/pages/OutboundOrder/pages/adhocShipOut"));
const OutboundOrdersCancelShipping = lazy(() => import("../pages/orders/pages/OutboundOrder/pages/cancelShipout"));
const OutboundOrdersCancelDeserializedShipping = lazy(() => import("../pages/orders/pages/OutboundOrder/pages/cancelDeserializedShipout"));
const AddOutboundOrder = lazy(() => import("../pages/orders/pages/OutboundOrder/pages/shipOutOrders/add"));
const EditOutboundOrder = lazy(() => import("../pages/orders/pages/OutboundOrder/pages/shipOutOrders/edit"));
const ScanShipOutOrderOutboundOrder = lazy(() => import("../pages/orders/pages/OutboundOrder/pages/shipOutOrders/scan"));
const AddCancelOrder = lazy(() => import("../pages/orders/pages/OutboundOrder/pages/cancelShipout/addCancelOrder"));
const AddCancelDeserializedOrder = lazy(() => import("../pages/orders/pages/OutboundOrder/pages/cancelDeserializedShipout/addCancelDeserializedOrder"));
const ScanOutboundAdhoc = lazy(() => import("../pages/orders/pages/OutboundOrder/pages/adhocShipOut/scan"));

// Outbound Vpoint Orders
const OutboundVpointOrders = lazy(() => import("../pages/orders/pages/OutboundVpointOrder/pages"));
const OutboundVpointOrdersShipOutByOrder = lazy(() => import("../pages/orders/pages/OutboundVpointOrder/pages/shipOutOrdersVpoint"));
const AddOutboundVpointOrder = lazy(() => import("../pages/orders/pages/OutboundVpointOrder/pages/shipOutOrdersVpoint/add"));
const EditOutboundVpointOrder = lazy(() => import("../pages/orders/pages/OutboundVpointOrder/pages/shipOutOrdersVpoint/edit"));
const ScanOutboundVpointOrder = lazy(() => import("../pages/orders/pages/OutboundVpointOrder/pages/shipOutOrdersVpoint/scan"));
const OutboundVpointOrdersAdhoc = lazy(() => import("../pages/orders/pages/OutboundVpointOrder/pages/adhocShipOutVpoint"));
const ScanOutboundVpointAdhoc = lazy(() => import("../pages/orders/pages/OutboundVpointOrder/pages/adhocShipOutVpoint/scan"));

// Commissioning Orders
const CommissioningOrder = lazy(() => import("../pages/orders/pages/CommissioningOrder/pages"));
const CommissioningOrderReceiveByOrder = lazy(() => import("../pages/orders/pages/CommissioningOrder/pages/receiveByOrder"));
const AddCommissioningOrder = lazy(() => import("../pages/orders/pages/CommissioningOrder/pages/receiveByOrder/add"));
const EditCommissioningOrder = lazy(() => import("../pages/orders/pages/CommissioningOrder/pages/receiveByOrder/edit"));
const ScanReceiveByOrderCommissioningOrder = lazy(() => import("../pages/orders/pages/CommissioningOrder/pages/receiveByOrder/scan"));

// Move Orders
const MoveOrders = lazy(() => import("../pages/moveOrders/pages"));
const AddMoveOrder = lazy(() => import("pages/moveOrders/pages/add"));
const EditMoveOrder = lazy(() => import("pages/moveOrders/pages/edit"));

// Files
const Files = lazy(() => import("../pages/files/Files"));
const AddFile = lazy(() => import("../pages/files/AddFile"));

// Partners
const Partners = lazy(() => import("../pages/partners/Partners"));
const AddPartner = lazy(() => import("../pages/partners/AddPartner"));
const AddPartnerSingle = lazy(() => import("../pages/partners/SingleEntryFields.js"));
const AddPartnerMultiple = lazy(() => import("../pages/partners/MultipleEntryFields.js"));
const EditPartner = lazy(() => import("../pages/partners/EditPartner"));

// Products
const Products = lazy(() => import("../pages/products/Products"));
const AddProduct = lazy(() => import("../pages/products/AddProduct"));
const AddSingleProduct = lazy(() => import("../pages/products/SingleEntryFields.js"));
const AddMultipleProduct = lazy(() => import("../pages/products/MultipleEntryFields.js"));
const EditProduct = lazy(() => import("../pages/products/EditProduct"));

// SKUs
const SKUs = lazy(() => import("../pages/skus/SKUs"));
const AddSKU = lazy(() => import("../pages/skus/AddSKU"));
const AddSKUSingle = lazy(() => import("../pages/skus/SingleEntryFields.js"));
const AddSKUMultiple = lazy(() => import("../pages/skus/MultipleEntryFields.js"));

// Inventory
const Mapping = lazy(() => import("../pages/inventory/Mapping"));
const AddMapping = lazy(() => import("../pages/inventory/AddMapping"));
const AddMappingSingle = lazy(() => import("../pages/inventory/SingleEntryFields.js"));
const AddMappingMultiple = lazy(() => import("../pages/inventory/MultipleEntryFields.js"));

// Reports
const AvailabilityReport = lazy(() => import("../pages/reports/AvailabilityReport"));
const AuditTrailReport = lazy(() => import("../pages/reports/history/AuditTrailReport"));
const SupplierReport = lazy(() => import("../pages/reports/SupplierReport"));
const QuantityReport = lazy(() => import("../pages/reports/QuantityReport"));
const DecantingReports = lazy(() => import("../pages/reports/DecantingReports"));
const ZohoAnalyticsIframe = lazy(() => import("../pages/reports/ZohoAnalyticsIframe"));
const BatchReport = lazy(() => import("../pages/reports/BatchReport"));
const BatchStatus = lazy(() => import("../pages/reports/BatchStatus"));
const NonSerializedStatus = lazy(() => import("../pages/reports/NonSerializedStatus"));

// Generators
const Generator = lazy(() => import("../pages/generators"));
const SerialGenerator = lazy(() => import("../pages/generators/generator/serial"));
const SSCCGenerator = lazy(() => import("../pages/generators/generator/sscc"));

// Control Tower
const WESAdapter = lazy(() => import("../pages/controlTower/wesAdapter"));
const WESAdapterTransactions = lazy(() => import("../pages/controlTower/wesAdapter/transactions"));
const WESAdapterTenants = lazy(() => import("../pages/controlTower/wesAdapter/tenants"));

// Roles & Users
const Roles = lazy(() => import("../pages/roles/Roles"));
const AddRole = lazy(() => import("../pages/roles/AddRole"));
const EditRole = lazy(() => import("../pages/roles/EditRole"));
const DuplicateRole = lazy(() => import("../pages/roles/DuplicateRole"));
const Users = lazy(() => import("../pages/users/Users"));
const AddUser = lazy(() => import("../pages/users/AddUser"));
const EditUser = lazy(() => import("../pages/users/EditUser"));

// Point of Sale
const PointOfSale = lazy(() => import("../pages/pointOfSale/PointOfSale"));

// Cancel Dispense
const CancelDispense = lazy(() => import("../pages/cancelDispense/CancelDispense"));

// Locations
const Location = lazy(() => import("../pages/locations/Location"));
const AddLocation = lazy(() => import("../pages/locations/AddLocation"));
const EditLocation = lazy(() => import("../pages/locations/EditLocation"));

// Verification & Activation
const VerifyStatus = lazy(() => import("../pages/verifyStatus/VerifyStatus"));
const Verification = lazy(() => import("../pages/verification/Verification"));
const Decommission = lazy(() => import("../pages/decommission/Decommission"));
const Activate = lazy(() => import("../pages/activate/Activate"));

// Pack / Unpack
const Pack = lazy(() => import("../pages/pack/Pack"));
const PackAggregation = lazy(() => import("../pages/pack/components/ByAggregation.js"));
const PackOrders = lazy(() => import("../pages/pack/components/ByOrder.js"));
const PackAdd = lazy(() => import("../pages/pack/components/ByAdd.js"));
const PackRemove = lazy(() => import("../pages/pack/components/ByRemove.js"));
const PackReprint = lazy(() => import("../pages/pack/components/Reprint.js"));
const PackScanOutbound = lazy(() => import("../pages/pack/components/byOrder/PackScanOutbound"));
const Unpack = lazy(() => import("../pages/unpack/Unpack"));
const UnpackParent = lazy(() => import("../pages/unpack/components/UnpackByParent.js"));
const UnpackChild = lazy(() => import("../pages/unpack/components/UnpackByChild.js"));

// Permit Numbers
const PermitNumber = lazy(() => import("../pages/permitNumber"));
const AddPermitNumber = lazy(() => import("../pages/permitNumber/addEdit"));
const EditPermitNumber = lazy(() => import("../pages/permitNumber/addEdit"));

// Cycle Count
const CreateCycleCount = lazy(() => import("../pages/cycleCount/CreateCycleCount"));
const UpdateCycleCount = lazy(() => import("../pages/cycleCount/UpdateCycleCount"));
const ScanCycleCount = lazy(() => import("../pages/cycleCount/ScanCycleCount"));
const ReportCycleCount = lazy(() => import("../pages/cycleCount/ReportCycleCount"));
const AdhocCycleCount = lazy(() => import("../pages/cycleCount/AdhocCycleCount"));

// Admin Panel
const AdminPanel = lazy(() => import("../pages/admin-panel/AdminPanel"));
const TenantSettings = lazy(() => import("pages/admin-panel/TenantSettings/index.js"));

// Change Password
const ChangePassword = lazy(() => import("../pages/changePassword/ChangePassword"));

const AdminLayoutWrapper = (
    <AuthGuard>
        <AdminLayout />
    </AuthGuard>
);

const authRoutes = (
    <Route element={AdminLayoutWrapper}>
        <Route path='/home' element={<Home/>}/>

        <Route path='/inbound' element={<PermissionGuard
            permissions={[permissions.InboundView]}><InboundOrders/></PermissionGuard>}>

            <Route path='receive-by-order' element={<InboundOrdersReceiveByOrder/>}/>

            <Route path='adhoc' element={<InboundOrdersAdhoc/>}/>

            <Route path='add-to-stock' element={<InboundOrdersAddToStock/>}/>

            <Route path='comply-receive' element={<InboundOrdersComplyReceive/>}/>

            <Route path='blind-receive' element={<InboundOrdersBlindReceive/>}/>
        </Route>
        <Route path='/inbound/receive-by-order/add' element={<PermissionGuard
            permissions={[permissions.InboundCreateOrder]}><AddInboundOrder/></PermissionGuard>}/>


        <Route path='/inbound/receive-by-order/:id/edit' element={<PermissionGuard
            permissions={[permissions.InboundUpdateOrder]}><EditInboundOrder/></PermissionGuard>}/>


        <Route path='/inbound/receive-by-order/:id/split' element={<PermissionGuard
            permissions={[permissions.InboundUpdateOrder]}><SplitInboundOrder/></PermissionGuard>}/>


        <Route path='/inbound/receive-by-order/:id/scan' element={<PermissionGuard
            permissions={[permissions.InboundReleaseToQuality, permissions.InboundAccept, permissions.InboundReject]}><ScanReceiveByOrderInboundOrder/></PermissionGuard>}/>

        <Route path='/inbound/adhoc/:id/scan' element={<PermissionGuard
            permissions={[permissions.InboundAccept]}><ScanInboundAdhoc/></PermissionGuard>}/>

        <Route path='/inbound/adhoc/without-supplier' element={<PermissionGuard
            permissions={[permissions.AdhocWithoutSupplier]}><AdhocWithoutSupplier/></PermissionGuard>}/>

        <Route path='/inbound/comply-receive/add-comply-receive' element={<PermissionGuard
            permissions={[permissions.InboundAccept]}><AddComplyReceive/></PermissionGuard>}/>

        <Route path='/inbound/blind-receive/:id/scan' element={<PermissionGuard
            permissions={[permissions.InboundAccept]}><ScanBlindReceive/></PermissionGuard>}/>

        <Route path='/inbound/blind-receive/:id/add-aggregation' element={<PermissionGuard
            permissions={[permissions.InboundAccept]}><AggregationBlindReceive/></PermissionGuard>}/>

        <Route path='/outbound' element={<PermissionGuard
            permissions={[permissions.OutboundView]}><OutboundOrders/></PermissionGuard>}>

            <Route path='shipout-by-order' element={<OutboundOrdersShipOutByOrder/>}/>

            <Route path='adhoc' element={<OutboundOrdersAdhoc/>}/>

            <Route path='cancel-shipping' element={<OutboundOrdersCancelShipping/>}/>
            <Route path='cancel-deserialized-shipping' element={<OutboundOrdersCancelDeserializedShipping/>}/>
        </Route>

        <Route path='/outbound/shipout-by-order/add' element={<PermissionGuard
            permissions={[permissions.OutboundCreateOrder]}><AddOutboundOrder/></PermissionGuard>}/>


        <Route path='/outbound/shipout-by-order/:id/edit' element={<PermissionGuard
            permissions={[permissions.OutboundUpdateOrder]}><EditOutboundOrder/></PermissionGuard>}/>


        <Route path='/outbound/shipout-by-order/:id/scan' element={<PermissionGuard
            permissions={[permissions.OutboundCreateOrder]}><ScanShipOutOrderOutboundOrder/></PermissionGuard>}/>

        <Route path='/outbound/adhoc/:id/scan' element={<PermissionGuard
            permissions={[permissions.OutboundCreateOrder]}><ScanOutboundAdhoc/></PermissionGuard>}/>

        <Route path='/outbound/cancel-shipping/add-cancel-order' element={<PermissionGuard
            permissions={[permissions.InboundAccept]}><AddCancelOrder/></PermissionGuard>}/>

        <Route path='/outbound/cancel-deserialized-shipping/add-cancel-deserialized-order' element={<PermissionGuard
            permissions={[permissions.InboundAccept]}><AddCancelDeserializedOrder/></PermissionGuard>}/>

        <Route path='/commissioning' element={<PermissionGuard
            permissions={[permissions.CommissionOrder]}><CommissioningOrder/></PermissionGuard>}>

            <Route path='receive-by-order' element={<CommissioningOrderReceiveByOrder/>}/>

        </Route>

        <Route path='/commissioning/receive-by-order/add' element={<PermissionGuard
            permissions={[permissions.CommissionOrder]}><AddCommissioningOrder/></PermissionGuard>}/>


        <Route path='/commissioning/receive-by-order/:id/edit' element={<PermissionGuard
            permissions={[permissions.CommissionOrder]}><EditCommissioningOrder/></PermissionGuard>}/>


        <Route path='/commissioning/receive-by-order/:id/scan' element={<PermissionGuard
            permissions={[permissions.CommissionOrder]}>
            <ScanReceiveByOrderCommissioningOrder/>
        </PermissionGuard>}/>

        <Route path='/outbound-vpoint' element={<PermissionGuard
            permissions={[permissions.VPointOutboundView]}><OutboundVpointOrders/></PermissionGuard>}>

            <Route path='shipout-by-order' element={<OutboundVpointOrdersShipOutByOrder/>}/>

            <Route path='adhoc' element={<OutboundVpointOrdersAdhoc/>}/>
        </Route>

        <Route path='/outbound-vpoint/shipout-by-order/add' element={<PermissionGuard
            permissions={[permissions.VPointOutboundCreateOrder]}><AddOutboundVpointOrder/></PermissionGuard>}/>


        <Route path='/outbound-vpoint/shipout-by-order/:id/edit' element={<PermissionGuard
            permissions={[permissions.VPointOutboundUpdateOrder]}><EditOutboundVpointOrder/></PermissionGuard>}/>


        <Route path='/outbound-vpoint/shipout-by-order/:id/scan' element={<PermissionGuard
            permissions={[permissions.VPointOutboundCreateOrder]}><ScanOutboundVpointOrder/></PermissionGuard>}/>

        <Route path='/outbound-vpoint/adhoc/:id/scan' element={<PermissionGuard
            permissions={[permissions.VPointOutboundCreateOrder]}><ScanOutboundVpointAdhoc/></PermissionGuard>}/>



        {/*<Route path='/serialization' element={<PermissionGuard*/}
        {/*    permissions={[permissions.SO_View]}><Serialization/></PermissionGuard>}/>*/}


        {/*<Route path='/serialization/add' element={<PermissionGuard*/}
        {/*    permissions={[permissions.SO_Add]}><AddSerializationOrder/></PermissionGuard>}/>*/}


        {/*<Route path='/serialization/:id/Edit' element={<PermissionGuard*/}
        {/*    permissions={[permissions.SO_Edit]}><EditSerializationOrder/></PermissionGuard>}/>*/}


        <Route path='/move-orders' element={<PermissionGuard
            permissions={[permissions.MO_View]}><MoveOrders/></PermissionGuard>}/>


        <Route path='/move-orders/add' element={<PermissionGuard
            permissions={[permissions.MO_Add]}><AddMoveOrder/></PermissionGuard>}/>

        <Route path='/move-orders/:id/edit' element={<PermissionGuard
            permissions={[permissions.MO_Edit]}><EditMoveOrder/></PermissionGuard>}/>


        <Route path='/files' element={<PermissionGuard
            permissions={[permissions.InboundFileView]}><Files/></PermissionGuard>}/>

        <Route path='/files/add' element={<PermissionGuard
            permissions={[permissions.InboundCreateInboundFile]}><AddFile/></PermissionGuard>}/>


        {/*<Route path='/machines' element={<PermissionGuard*/}
        {/*    permissions={[permissions.Machine_View]}><Machines/></PermissionGuard>}/>*/}


        {/*<Route path='/machines/add' element={<PermissionGuard*/}
        {/*    permissions={[permissions.Machine_Add]}><AddMachine/></PermissionGuard>}/>*/}


        {/*<Route path='/machines/:id/Edit' element={<PermissionGuard*/}
        {/*    permissions={[permissions.Machine_Edit]}><EditMachine/></PermissionGuard>}/>*/}


        <Route path='/partners' element={<PermissionGuard
            permissions={[permissions.PartnerView]}><Partners/></PermissionGuard>}/>


        <Route path='/partners/add' element={<PermissionGuard
            permissions={[permissions.PartnerCreate]}><AddPartner/></PermissionGuard>}>
            <Route path='single' element={<AddPartnerSingle/>}/>
            <Route path='multiple' element={<AddPartnerMultiple/>}/>
        </Route>


        <Route path='/partners/:id/edit' element={<PermissionGuard
            permissions={[permissions.PartnerCreate]}><EditPartner/></PermissionGuard>}/>


        <Route path='/products' element={<PermissionGuard
            permissions={[permissions.ProductView]}><Products/></PermissionGuard>}/>


        <Route path='/products/add' element={<PermissionGuard
            permissions={[permissions.ProductCreate]}><AddProduct/></PermissionGuard>}>
            <Route path='single' element={<AddSingleProduct/>}/>
            <Route path='multiple' element={<AddMultipleProduct/>}/>
        </Route>


        <Route path='/products/:id/edit' element={<PermissionGuard
            permissions={[permissions.ProductEdit]}><EditProduct/></PermissionGuard>}/>


        <Route path='/skus' element={<SKUs/>}/>

        <Route path='/skus/add' element={<AddSKU/>}>
            <Route path='single' element={<AddSKUSingle/>}/>
            <Route path='multiple' element={<AddSKUMultiple/>}/>
        </Route>


        <Route path='/mapping' element={<Mapping/>}/>

        <Route path='/mapping/add' element={<AddMapping/>}>
            <Route path='single' element={<AddMappingSingle/>}/>
            <Route path='multiple' element={<AddMappingMultiple/>}/>
        </Route>


        <Route path='/availability-report' element={<PermissionGuard
            permissions={[permissions.ViewProductAvailabilityReport]}><AvailabilityReport/></PermissionGuard>}/>


        <Route path='/auditTrail-report' element={<PermissionGuard
            permissions={[permissions.ViewProductAvailabilityReport]}><AuditTrailReport/></PermissionGuard>}/>


        <Route path='/supplier-report' element={<PermissionGuard
            permissions={[permissions.ViewProductBySupplierReport]}><SupplierReport/></PermissionGuard>}/>


        <Route path='/quantity-report' element={<PermissionGuard
            permissions={[permissions.ViewProductQuantityReport]}><QuantityReport/></PermissionGuard>}/>


        <Route path='/decanting-report' element={<PermissionGuard
            permissions={[permissions.ViewProductQuantityReport]}><DecantingReports/></PermissionGuard>}/>


        <Route path='/analytics-dashboard' element={<PermissionGuard
            permissions={[permissions.DashboardView]}><ZohoAnalyticsIframe/></PermissionGuard>}/>


        <Route path='/batch-availability' element={<PermissionGuard
            permissions={[permissions.ViewProductQuantityReport]}><BatchReport/></PermissionGuard>}/>


        <Route path='/batch-status' element={<PermissionGuard
            permissions={[permissions.ViewProductQuantityReport]}><BatchStatus/></PermissionGuard>}/>


        <Route path='/non-serialized' element={<PermissionGuard
            permissions={[permissions.ViewProductQuantityReport]}><NonSerializedStatus/></PermissionGuard>}/>

        <Route path='/generator' element={<PermissionGuard
            permissions={[permissions.SGTINGenerator, permissions.SSCCGenerator]}><Generator/></PermissionGuard>}>
            <Route path='serial' element={
                <PermissionGuard
                    permissions={[permissions.SGTINGenerator]}><SerialGenerator/></PermissionGuard>}/>
            <Route path='sscc' element={<PermissionGuard
                permissions={[permissions.SSCCGenerator]}><SSCCGenerator/></PermissionGuard>}/>
        </Route>

        <Route path='/wes-adapter' element={<PermissionGuard
            permissions={[permissions.InputAdaptorView]}><WESAdapter/></PermissionGuard>}>
            <Route path='transactions' element={<WESAdapterTransactions/>}/>

            <Route path='tenants' element={<WESAdapterTenants/>}/>
        </Route>

        <Route path='/roles' element={<PermissionGuard
            permissions={[permissions.UserManagement]}><Roles/></PermissionGuard>}/>

        <Route path='/roles/add' element={<PermissionGuard
            permissions={[permissions.UserManagement]}><AddRole/></PermissionGuard>}/>

        <Route path='/roles/:id/edit' element={<PermissionGuard
            permissions={[permissions.UserManagement]}><EditRole/></PermissionGuard>}/>

        <Route path='/roles/:id/duplicate' element={<PermissionGuard
            permissions={[permissions.UserManagement]}><DuplicateRole/></PermissionGuard>}/>

        <Route path='/users' element={<PermissionGuard
            permissions={[permissions.UserManagement]}><Users/></PermissionGuard>}/>

        <Route path='/users/add' element={<PermissionGuard
            permissions={[permissions.UserManagement]}><AddUser/></PermissionGuard>}/>

        <Route path='/users/:id/edit' element={<PermissionGuard
            permissions={[permissions.UserManagement]}><EditUser/></PermissionGuard>}/>

        <Route path='/location' element={<PermissionGuard
            permissions={[permissions.ViewSubInventories]}><Location/></PermissionGuard>}/>

        <Route path='/location/add' element={<PermissionGuard
            permissions={[permissions.CreateSubInventory]}><AddLocation/></PermissionGuard>}/>

        <Route path='/location/:id/edit' element={<PermissionGuard
            permissions={[permissions.UpdateSubInventory]}><EditLocation/></PermissionGuard>}/>

        <Route path='/point-of-sale' element={<PermissionGuard
            permissions={[permissions.POS_Sell]}><PointOfSale/></PermissionGuard>}/>

        <Route path='/cancel-dispense' element={<PermissionGuard permissions={[permissions.CancelDispense]}>
            <CancelDispense/></PermissionGuard> } />

        <Route path='/verify-status' element={<PermissionGuard
            permissions={[permissions.Verify_Status]}><VerifyStatus/></PermissionGuard>}/>

        <Route path='/verification' element={<PermissionGuard
            permissions={[permissions.ItemVerification]}><Verification/></PermissionGuard>}/>

        <Route path='/decommission' element={<PermissionGuard
            permissions={[permissions.DecomissionView]}><Decommission/></PermissionGuard>}/>

        <Route path='/activate' element={<PermissionGuard
            permissions={[permissions.Activate]}><Activate/></PermissionGuard>}/>

        <Route path='/pack' element={<PermissionGuard
            permissions={[permissions.PackView]}><Pack/></PermissionGuard>}>
            <Route path='aggregation' element={<PackAggregation/>}/>
            <Route path='orders' element={<PackOrders/>}/>
            <Route path='add' element={<PackAdd/>}/>
            <Route path='remove' element={<PackRemove/>}/>
            <Route path='reprint' element={<PackReprint/>}/>
        </Route>

        <Route path='/pack/orders/:id/order_pack' element={<PermissionGuard
            permissions={[permissions.Disaggregation]}><PackScanOutbound/></PermissionGuard>}/>

        <Route path='/unpack' element={<PermissionGuard
            permissions={[permissions.Disaggregation]}><Unpack/></PermissionGuard>}>
            <Route path='parent' element={<UnpackParent/>}/>
            <Route path='child' element={<UnpackChild/>}/>
        </Route>

        <Route path='/permit-number' element={<PermissionGuard
            permissions={[permissions.ViewPermitNumber]}><PermitNumber/></PermissionGuard>}/>

        <Route path='/permit-number/add' element={<PermissionGuard
            permissions={[permissions.CreatePermitNumber]}><AddPermitNumber/></PermissionGuard>}/>

        <Route path='/permit-number/:id/edit' element={<PermissionGuard
            permissions={[permissions.EditPermitNumber]}><EditPermitNumber/></PermissionGuard>}/>

        {/*<Route path='/cycle-count/scan-adhoc' element={<PermissionGuard*/}
        {/*    permissions={[permissions.CycleCount_View]}><CycleCount/></PermissionGuard>}/>*/}

        <Route path='/cycle-count/create' element={<PermissionGuard
            permissions={[permissions.CycleCount_Create]}><CreateCycleCount/></PermissionGuard>}/>

        <Route path='/cycle-count/:id/Update' element={<PermissionGuard
            permissions={[permissions.CycleCount_Create]}><UpdateCycleCount/></PermissionGuard>}/>

        <Route path='/cycle-count/:id/Scan' element={<PermissionGuard
            permissions={[permissions.CycleCount_Create]}><ScanCycleCount/></PermissionGuard>}/>

        <Route path="/cycle-count/:id/report" element={<PermissionGuard
            permissions={[permissions.CycleCount_View]}><ReportCycleCount/></PermissionGuard>}/>

        <Route path="/cycle-count/scan-adhoc" element={<PermissionGuard
            permissions={[permissions.AdhocCycleCount_Create]}><AdhocCycleCount/></PermissionGuard>}/>

        <Route path="/admin-panel" element={<PermissionGuard
            permissions={[permissions.ViewTenantSetting]}><AdminPanel/></PermissionGuard>}/>

        <Route path="/admin-panel/:id/tenant-settings" element={<PermissionGuard
            permissions={[permissions.UpdateTenantSettings]}><TenantSettings/></PermissionGuard>}/>

        <Route path='/change-password'
               element={<ChangePassword/>}/>
    </Route>
);

export default authRoutes;
